import React from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentAdmin } from '@/redux/auth/selectors';
import {
    InboxOutlined,
    AppstoreOutlined,
    ProjectOutlined,
    TeamOutlined,
    FlagOutlined,
    CalendarOutlined,
    BarChartOutlined,
    FileTextOutlined,
    FolderOpenOutlined,
    LeftOutlined,
    RightOutlined,
} from '@ant-design/icons';

/**
 * ProjectSidebar — fixed to the viewport, flush with the top of the page.
 * It sits behind the sticky header (z-40 < header z-[1000]) so it visually
 * "touches" the header at all times without overlapping it.
 *
 * Expanded : w-64 (256px)  — slightly wider for comfortable reading
 * Collapsed: w-14 (56px)   — icon-only rail
 *
 * Auto-closes on screens narrower than 1024px (lg breakpoint).
 * The parent (index.jsx) adds a matching left margin to the main content.
 */

const NAV_ITEMS = [
    { key: 'inbox', label: 'Inbox', icon: <InboxOutlined /> },
    { key: 'overview', label: 'Task Management', icon: <AppstoreOutlined /> },
    { key: 'dashboard', label: 'Project Dashboard', icon: <ProjectOutlined /> },
    { key: 'files', label: 'Project Files', icon: <FolderOpenOutlined /> },
    { key: 'teams', label: 'Teams', icon: <TeamOutlined /> },
    { key: 'goals', label: 'Goals', icon: <FlagOutlined /> },
    { key: 'milestones', label: 'Milestones', icon: <CalendarOutlined /> },
    { key: 'analytics', label: 'Analytics', icon: <BarChartOutlined /> },
    { key: 'reports', label: 'Reports', icon: <FileTextOutlined /> },
];

export default function ProjectSidebar({ isOpen, onToggle, activeNav = 'overview', onNavChange, inboxBadgeCount = 0 }) {
    const currentAdmin = useSelector(selectCurrentAdmin);
    const role = currentAdmin?.role;

    const filteredNavItems = NAV_ITEMS.filter(item => {
        if (role === 'Client') {
            return ['inbox', 'dashboard', 'overview', 'milestones', 'files'].includes(item.key);
        }
        if (role === 'Employee') {
            return ['inbox', 'dashboard', 'overview', 'goals', 'milestones', 'reports', 'files'].includes(item.key);
        }
        // Admin, super_admin, Manager see everything
        return true;
    });

    return (
        /* Fixed wrapper — starts at top:0 so it is flush with the page edge.
           The sticky header (z-[1000]) sits on top visually, giving the
           appearance of the sidebar always touching the header. */
        <div
            className={`
                fixed top-0 left-0 bottom-0 z-40
                transition-all duration-300 ease-in-out
                ${isOpen ? 'w-64' : 'w-14'}
            `}
        >
            {/* Rail — pt-14 pushes content below the 56px header */}
            <aside className="
                h-full w-full overflow-hidden
                bg-white dark:bg-[#1f1f1f]
                border-r border-[#e7e8e9] dark:border-[#303030]
                flex flex-col pt-14
            ">
                <nav className="flex-1 px-2 pt-5 pb-4 flex flex-col gap-1 overflow-y-auto overflow-x-hidden">
                    {filteredNavItems.map((item) => {
                        const active = activeNav === item.key;
                        return (
                            <button
                                key={item.key}
                                onClick={() => onNavChange?.(item.key)}
                                title={!isOpen ? item.label : undefined}
                                className={`
                                    w-full flex items-center gap-3 rounded-xl
                                    text-[15px] font-semibold text-left whitespace-nowrap
                                    transition-all duration-150 cursor-pointer
                                    ${isOpen ? 'px-3 py-3' : 'px-0 py-3 justify-center'}
                                    ${active
                                        ? 'bg-[#eef4fb] dark:bg-[#262626] text-[#1e3a5f] dark:text-[#00AAF1]'
                                        : 'text-[#4a5568] dark:text-slate-400 hover:bg-[#f3f6fa] dark:hover:bg-[#262626] hover:text-[#1e3a5f] dark:hover:text-white'
                                    }
                                `}
                            >
                                <span className={`
                                    text-[19px] flex-shrink-0 flex items-center justify-center
                                    ${active ? 'text-[#1e3a5f] dark:text-[#00AAF1]' : 'text-[#7a8499] dark:text-slate-500'}
                                `}>
                                    {item.icon}
                                </span>
                                {isOpen && (
                                    <span className="truncate leading-none flex-1">{item.label}</span>
                                )}
                                {isOpen && (item.key === 'inbox' ? inboxBadgeCount > 0 : Boolean(item.badge)) && (
                                    <span className="bg-[#03488A] dark:bg-[#00AAF1] text-white dark:text-[#0d1117] text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 shadow-xs">
                                        {item.key === 'inbox' ? inboxBadgeCount : item.badge}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </nav>
            </aside>

            {/* Toggle pill on the right edge of the fixed wrapper */}
            <button
                onClick={onToggle}
                title={isOpen ? 'Collapse' : 'Expand'}
                className="
                    absolute top-1/2 -translate-y-1/2 -right-4
                    w-8 h-14 rounded-full z-50
                    bg-white dark:bg-[#1f1f1f]
                    border border-[#e7e8e9] dark:border-[#303030]
                    flex items-center justify-center
                    text-[#7a8499] dark:text-slate-400
                    hover:text-[#1e3a5f] dark:hover:text-white
                    hover:border-[#b0bcd4] dark:hover:border-slate-400
                    shadow-sm transition-colors duration-150
                "
            >
                {isOpen
                    ? <LeftOutlined style={{ fontSize: 11 }} />
                    : <RightOutlined style={{ fontSize: 11 }} />
                }
            </button>
        </div>
    );
}
